"use client"

import { RefreshCw, TrendingDown, TrendingUp, ExternalLink, Search, ChevronDown, MoreVertical } from "lucide-react"
import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux";
import { getCoins, refreshCoin } from "../redux/slices/coinsSlice";
import { useBlur } from '../context/BlurContext';
import { openCoinMarketCap } from "../utils/coinMarketUtils";
import { formatBalance, formatCryptoBalance, formatUSDBalance } from "../utils/formatBalance";
import { useNavigate } from "react-router-dom";
import { Dropdown, OverlayTrigger, Tooltip } from 'react-bootstrap';
import WalletDashboardCard from "./WalletDashboardCard";
import SendModal from "./SendModal";
import ReceiveModal from "./ReceiveModal";
import { ShieldModal } from "./shield";
import PrivateTransferModal from "./InternalTransferModal";
import CoinDetailsModal from "./CoinDetailsModal";
import WelcomeBonusModal from "./WelcomeBonusModal";
import ShieldIconSvg from "../assets/svg/ShieldIconSvg";

import UnshieldIconSvg from "../assets/svg/UnshieldIconSvg";
import TransactionStatusLoader from "./TransactionStatusLoader";
import { showToast } from "../redux/slices/toastSlice";

import DashboardListingSkeleton from "./skeletons/DashboardListingSkeleton";
import { useTour } from "../hooks/useTour";
import { syncWallet } from "../redux/slices/walletSlice";
import { useDebounce } from "../hooks";


function WalletDashboard() {
  const dispatch = useDispatch();
  const { blurred } = useBlur();
  const mode = useSelector(state => state.mode.mode);
  const chains = useSelector(state => state.coins.chains);
  const loading = useSelector(state => state.coins.loading);
  const refreshingCoins = useSelector(state => state.coins.refreshingCoins);
  const { startTour } = useTour();


  const [sendModalOpen, setSendModalOpen] = useState(false)
  const [receiveModalOpen, setReceiveModalOpen] = useState(false)
  const [shieldUnshieldModalOpen, setShieldUnshieldModalOpen] = useState(false)
  const [privateTransferModalOpen, setPrivateTransferModalOpen] = useState(false)
  const [coinDetailsModalOpen, setCoinDetailsModalOpen] = useState(false)
  const [selectedToken, setSelectedToken] = useState(null)
  const [selectedChain, setSelectedChain] = useState(null)

  const [activeChain, setActiveChain] = useState("all");
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);


  const navigate = useNavigate(); // ✅ ADD THIS
  // navigate(`/coin/${chain.id}/${token.id}`)

  // Initial load of coins - only on component mount
  useEffect(() => {
    // Only fetch if we don't have data yet
    if (chains.length === 0) {
      dispatch(getCoins(mode));
    }

    // Check for welcome bonus flag
    const welcomePending = localStorage.getItem('kaxaa_welcome_bonus_pending');
    if (welcomePending) {
      setWelcomeModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  // Auto-start tour on first visit
  useEffect(() => {
    if (!loading && chains.length > 0 && !welcomeModalOpen) {
      const timer = setTimeout(() => {
        startTour(false);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [loading, chains, startTour, welcomeModalOpen]);

  const handleCloseWelcomeModal = () => {
    setWelcomeModalOpen(false);
    localStorage.removeItem('kaxaa_welcome_bonus_pending');
  };



  const handleShieldUnshieldClick = (token) => {
    if (!token.isShieldable) {
      dispatch(showToast({ msg: "This coin is not shieldable", state: "warning" }));
      return;
    }
    // Enhance token with address information based on mode
    const enhancedToken = {
      ...token,
      publicAddress: token.walletAddress || token.publicAddress || '0x...',
      privateAddress: token.privateAddress || '0zk...' + token.walletAddress?.slice(-6) || '0zk...abc123'
    }
    setSelectedToken(enhancedToken)
    setShieldUnshieldModalOpen(true)
  }

  const handleCoinRefresh = async (coinId) => {
    console.log('Refreshing coin:', coinId, 'Mode:', mode);
    console.log('Current refreshingCoins state:', refreshingCoins);

    try {
      // Refresh the specific coin
      await dispatch(refreshCoin({ coinId, mode }));
      console.log('Coin refresh completed successfully');
    } catch (error) {
      console.error('Failed to refresh coin:', error);
    }
  }

  const activeTransaction = chains
    .flatMap(chain => chain.coins)
    .find(token => token.transactionStatus);

  const filteredChains =
    (activeChain === "all"
      ? chains
      : chains.filter(chain => chain.id === activeChain)
    ).map(chain => {
      // Filter coins within each chain based on search query
      const filteredCoins = chain.coins.filter(token => {
        if (!debouncedSearchQuery) return true;
        const query = debouncedSearchQuery.toLowerCase();
        return (
          token.name.toLowerCase().includes(query) ||
          token.symbol.toLowerCase().includes(query)
        );
      });
      return { ...chain, coins: filteredCoins };
    }).filter(chain => chain.coins.length > 0);

  return (
    <>
      <div className="wallet-dashboard main-page">
        {/* <div className="shield-notice">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="16" r="1" /><rect width="18" height="12" x="3" y="10" rx="2" /><path d="M7 10V7a5 5 0 0 1 9.33-2.5" /></g></svg>
          <span>Shield ERC20 & Polygon assets through your Kaxaa wallet Account.</span>
        </div> */}
        <div className={`grid lg:grid-cols-3 md:gap-5`}>
          <WalletDashboardCard />
          <div id="tour-shield-card" className="account-card w-full flex align-items-center sheild-card"
          // style={{backgroundImage: 'url(/assets/sheild.png)'}}
          >
            <div className="flex items-start justify-between gap-3 flex-1">

              {/* Center - Main Content */}
              <div className="flex-1">
                <h1 className="text-2xl font-semibold mb-2 text-balance" style={{ lineHeight: '1.2' }}>
                  {(activeTransaction?.transactionStatus?.status === 'PENDING' || activeTransaction?.transactionStatus?.status === 'IN_PROGRESS') ?
                    <>
                      <span className=''>
                        {mode !== 'private' ? "Shield" : "UnShield"}
                      </span> your tokens with
                    </>
                    :
                    <>
                      Kaxaa supports <br /> <span className="text-emerald-600">Zero-Knowledge Privacy </span>
                    </>
                  }
                </h1>
                <p className="m-0 mt-3 text-gray-600 w-11/12" style={{ lineHeight: '1.3' }}>
                  {(activeTransaction?.transactionStatus?.status === 'PENDING' || activeTransaction?.transactionStatus?.status === 'IN_PROGRESS') ?
                    <>
                      {mode !== 'private' ? "Kaxaa Zero-Knowledge (ZK) private eWallet technology" : "Unshielding funds to withdrawn Tokens from the private Zero-Knowledge private eWallet to public eWallet"}
                      {/* Unshielding funds to withdrawn Tokens from the private Zero-Knowledge private eWallet to public eWallet */}
                    </>
                    :
                    <>
                      for Polygon and Ethereum networks, providing protection that keeps your balances and transaction activity from being publicly exposed.
                    </>
                  }
                </p>
              </div>

              <div className="flex flex-col items-center">
                <div class="p-0 icon-holder opacity-50 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="66" height="66" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-width="1.2"><path d="M3 10.417c0-3.198 0-4.797.378-5.335c.377-.537 1.88-1.052 4.887-2.081l.573-.196C10.405 2.268 11.188 2 12 2s1.595.268 3.162.805l.573.196c3.007 1.029 4.51 1.544 4.887 2.081C21 5.62 21 7.22 21 10.417v1.574c0 5.638-4.239 8.375-6.899 9.536C13.38 21.842 13.02 22 12 22s-1.38-.158-2.101-.473C7.239 20.365 3 17.63 3 11.991z"></path><path stroke-linecap="round" d="M12 13.5v3m1.5-3.402a3 3 0 1 1-3-5.195a3 3 0 0 1 3 5.195Z"></path></g></svg>
                </div>
                {/* {mode === 'private' && (
                  <div className="mt-2 text-center">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="copy-tooltip">Refresh your private balance</Tooltip>}
                    >
                      <button
                        className="btn-balance"
                        onClick={handleManualSync}
                        disabled={syncStatus === 'syncing'}
                      >
                        <RefreshCw className={`w-5 h-5 text-primary ${syncStatus === 'syncing' ? 'refresh-rotating' : ''}`} />
                      </button>
                    </OverlayTrigger>
                    {lastSyncAt && (
                      <div className="text-10 text-gray-500 mt-1 whitespace-nowrap">
                        Last synced: {moment(lastSyncAt).format('HH:mm:ss')}
                      </div>
                    )}
                  </div>
                )} */}
              </div>

            </div>
          </div>
        </div>
        {loading ?
          <DashboardListingSkeleton />
          :
          <>

            <div className="dashboard-grid mt-2">
              <div className="lg:flex justify-between gap-2 mb-4 items-center table-filter">
                <div className="relative" id="tour-search">
                  <input
                    type="text"
                    className="form-control m-0 lg:w-auto"
                    style={{ minWidth: '300px' }}
                    placeholder="Search by keyword..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <span className="absolute left-4" style={{ top: '50%', transform: 'translateY(-50%)' }}>
                    <Search size={18} className="text-gray-500" />
                  </span>
                </div>
                <div className="on-mobile--scroll">
                  <div className="dashboard-holder mt-lg-0 mt-4">
                    <span
                      className={activeChain === "all" ? "active" : ""}
                      onClick={() => setActiveChain("all")}
                    >All</span>
                    {chains.map((chain, index) =>
                      <span
                        key={index}
                        className={activeChain === chain.id ? "active" : ""}
                        onClick={() => setActiveChain(chain.id)}
                      >{chain.name}</span>
                    )}
                  </div>
                </div>
              </div>


              {/* Mobile Card List */}
              <div className="lg:hidden space-y-4" id="tour-token-list-mobile">
                {filteredChains.map((chain, index) =>
                  chain.coins.map((token, tokenIndex) => (
                    <div
                      key={`mobile-${index}-${tokenIndex}`}
                      className="wallet-card-mobile bg-white p-3 rounded-xl border border-gray-100 shadow-sm"
                      onClick={() => navigate(`/coin/${chain.id}/${token.id}`)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <img
                            src={token.image}
                            alt={token.name}
                            className="w-10 h-10 rounded-full border"
                          />
                          <div>
                            <div className="font-semibold text-18">{token.name}</div>
                            <div className="text-secondary text-14">{chain.symbol}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-bold text-14">{formatCryptoBalance(token.balance, token.symbol)}</div>
                          <div className="text-secondary text-14">≈ {formatUSDBalance(token.balance * token.price?.price)}</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between mt-1 pt-3">
                        <div className="flex items-center gap-4">
                          <div className="text-14">
                            <span className="text-gray-500">Price: </span>
                            <span className="font-medium">${parseFloat(token.price?.price).toFixed(2)}</span>
                          </div>
                          <div className={`text-14 flex items-center ${token.price?.change >= 0 ? 'text-success' : 'text-danger'}`}>
                            {parseFloat(token.price?.change || 0).toFixed(2)}%
                            {token.price?.change >= 0 ? <TrendingUp size={12} className="ml-1" /> : <TrendingDown size={12} className="ml-1" />}
                          </div>
                        </div>

                        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                          <button
                            className="p-2 border btn-icon rounded-lg"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCoinRefresh(token.id);
                            }}
                          >
                            <RefreshCw size={16} className={refreshingCoins[token.id] ? 'refresh-rotating' : ''} />
                          </button>

                          <Dropdown align="end" drop="up">
                            <Dropdown.Toggle variant="light" className="btn-icon border p-1 rounded-lg">
                              <MoreVertical size={16} />
                            </Dropdown.Toggle>
                            <Dropdown.Menu className="shadow rounded-3">
                              {mode === 'private' ? (
                                <>
                                  <Dropdown.Item onClick={() => { setPrivateTransferModalOpen(true); setSelectedToken(token); }}>Private Transfer</Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleShieldUnshieldClick(token)}>Unshield</Dropdown.Item>
                                </>
                              ) : (
                                <>
                                  <Dropdown.Item onClick={() => { setSelectedToken(token); setSendModalOpen(true); }}>Send</Dropdown.Item>
                                  <Dropdown.Item onClick={() => { setReceiveModalOpen(true); setSelectedToken(token); }}>Receive</Dropdown.Item>
                                  <Dropdown.Item onClick={() => handleShieldUnshieldClick(token)}>Shield</Dropdown.Item>
                                </>
                              )}
                            </Dropdown.Menu>
                          </Dropdown>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="table-responsive hidden lg:block" id="tour-token-table">

                <table className="table align-middle mb-0">
                  <thead className="">
                    <tr>
                      <th className="">Name</th>
                      <th className="">Price (USD)</th>
                      <th className="">Change (24 hours)</th>
                      <th className="text-end">Balance</th>
                      {/* <th className="">Value</th> */}
                      <th className=" text-center" style={{ width: '70px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredChains.map((chain, index) =>
                      chain.coins.map((token, tokenIndex) => (
                        <tr key={`${index}-${tokenIndex}`} className="cursor-pointer transition-colors rounded-lg p-2"
                          id={`tour-token-${token.name.toLowerCase()}`}
                        >
                          <td className="" onClick={() => {
                            setSelectedToken(token)
                            setSelectedChain(chain)
                            navigate(`/coin/${chain.id}/${token.id}`)
                          }}>
                            <div className="flex items-center gap-2">
                              <img
                                src={token.image}
                                alt={token.name}
                                className="min-w-[30px] min-h-[30px] w-8 h-8 rounded-full coin-icon"
                              />
                              <div className="md:flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-nowrap ms-1">{token.name}</div>
                                  <div className="text-secondary text-14 me-4">{chain.symbol}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                  {/* {token.transactionStatus && (
                                    <TransactionStatusLoader transactionStatus={token.transactionStatus} />
                                  )} */}
                                </div>
                              </div>
                            </div>
                            {/* <div className="text-xs break-all mt-2">{token.walletAddress}</div> */}

                          </td>
                          <td>
                            {/* <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`price-${token.id}`}>Click to view on CoinMarketCap</Tooltip>}
                        > */}
                            <span
                              className="text-nowrap cursor-pointer hover:text-primary transition-colors"
                            // onClick={(e) => {
                            //   e.stopPropagation()
                            //   openCoinMarketCap(token)
                            // }}
                            >
                              {`$ ${parseFloat(token.price?.price).toFixed(2)}`}
                            </span>
                            {/* </OverlayTrigger> */}
                          </td>
                          <td>
                            {/* <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip id={`change-${token.id}`}>Click to view on CoinMarketCap</Tooltip>}
                        > */}
                            <span
                              className={`
                              flex items-center lg:w-auto w-full cursor-pointer hover:text-primary transition-colors
                              ${token.price?.change >= 0 ? 'text-success' : 'text-danger'}
                            `}
                            // onClick={(e) => {
                            //   e.stopPropagation()
                            //   openCoinMarketCap(token)
                            // }}
                            >
                              {parseFloat(token.price?.change || 0).toFixed(2)} %
                              {token.price?.change >= 0 ?
                                <TrendingUp color="green" className="ml-[6px] w-4 h-4 ml-1" />
                                :
                                <TrendingDown color="red" className="ml-[6px] w-4 h-4 ml-1" />}
                            </span>
                            {/* </OverlayTrigger> */}
                          </td>
                          <td>
                            <div className={`flex items-start justify-end gap-3 text-end`}>
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id="refresh-tooltip">{"Refresh"}</Tooltip>}
                              >
                                <button
                                  className="sheild-icon btn-icon"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleCoinRefresh(token.id)
                                  }}
                                  disabled={refreshingCoins[token.id]}
                                  style={{
                                    // backgroundColor: refreshingCoins[token.id] ? '#f0f0f0' : 'transparent',
                                    opacity: refreshingCoins[token.id] ? 0.7 : 1
                                  }}
                                >
                                  <RefreshCw className={`w-5 h-5 ${refreshingCoins[token.id] ? 'refresh-rotating' : ''}`} />
                                </button>
                              </OverlayTrigger>
                              <div>
                                <span>{formatCryptoBalance(token.balance, token.symbol)}</span>
                                <div className="font-medium ">≈ {formatUSDBalance(token.balance * token.price?.price)}</div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className={`relative flex justify-center gap-0 ${blurred ? 'secret blurred' : ''} `}>

                              <Dropdown align="end" drop="up" id="tour-action-menu">
                                <Dropdown.Toggle
                                  variant="light"
                                  className="btn btn-icon border p-0"
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></g></svg>
                                </Dropdown.Toggle>

                                <Dropdown.Menu className="shadow rounded-3 coin-dropdown-menu">
                                  {mode === 'private' ? (
                                    // Private mode - Private Transfer and History
                                    <>
                                      <Dropdown.Item onClick={() => {
                                        setPrivateTransferModalOpen(true)
                                        setSelectedToken(token)
                                      }}>Private Transfer</Dropdown.Item>
                                      <Dropdown.Item onClick={() => handleShieldUnshieldClick(token)}>
                                        Unshield
                                      </Dropdown.Item>
                                      <Dropdown.Divider />
                                      <Dropdown.Item
                                        onClick={() => {
                                          setSelectedToken(token)
                                          setSelectedChain(chain)
                                          // setCoinDetailsModalOpen(true)
                                          navigate(`/coin/${chain.id}/${token.id}`)
                                        }}
                                        className="d-flex align-items-center"
                                      >
                                        <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        Token Detail Page
                                      </Dropdown.Item>

                                    </>
                                  ) : (
                                    // Public mode - all options
                                    <>
                                      <Dropdown.Item onClick={() => {
                                        setSelectedToken(token)
                                        setSendModalOpen(true)
                                      }}>Send</Dropdown.Item>
                                      <Dropdown.Item onClick={() => {
                                        setReceiveModalOpen(true)
                                        setSelectedToken(token)
                                      }}>Receive</Dropdown.Item>
                                      <Dropdown.Item onClick={() => handleShieldUnshieldClick(token)}>
                                        Shield
                                      </Dropdown.Item>
                                      <Dropdown.Divider />
                                      <Dropdown.Item
                                        onClick={() => {
                                          setSelectedToken(token)
                                          setSelectedChain(chain)
                                          // setCoinDetailsModalOpen(true)
                                          navigate(`/coin/${chain.id}/${token.id}`)
                                        }}
                                        className="d-flex align-items-center"
                                      >
                                        <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                        Token Detail Page
                                      </Dropdown.Item>

                                    </>
                                  )}
                                </Dropdown.Menu>
                              </Dropdown>

                              {/* {token.isShieldable && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="shield-tooltip">
                              {token.transactionStatus ? 
                                `${token.transactionStatus.type} in progress...` : 
                                (mode === 'public' ? 'Shield' : 'Unshield')
                              }
                            </Tooltip>}
                          >
                            <button
                              className="sheild-icon btn-icon"
                              onClick={(e) => {
                                e.stopPropagation()
                                if (!token.transactionStatus) {
                                  handleShieldUnshieldClick(token)
                                }
                              }}
                              disabled={!!token.transactionStatus}
                              style={{ 
                                cursor: token.transactionStatus ? "not-allowed" : "pointer",
                                opacity: token.transactionStatus ? 0.5 : 1
                              }}
                            >
                              {mode === 'public' ? <ShieldIconSvg className="w-5 h-5" /> : <UnshieldIconSvg className="w-5 h-5" />}
                            </button>
                          </OverlayTrigger>
                        )}
                        
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip id="refresh-tooltip">{"View details"}</Tooltip>}
                          >
                          <span className="px-2 mt-1"
                              onClick={() => {
                              setSelectedToken(token)
                              setSelectedChain(chain)
                              // setCoinDetailsModalOpen(true)
                              navigate(`/coin/${chain.id}/${token.id}`)
                            }}
                          >
                            <svg class="bn-svg" width={22} height={22} alt="Detail" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M15.863 5.363a.9.9 0 011.274 1.274l-4.147 4.146a1.4 1.4 0 01-1.98 0L10 9.773l-1.863 1.864a.9.9 0 01-1.274-1.274L9.01 8.217l.106-.096a1.4 1.4 0 011.768 0l.106.096L12 9.227l3.863-3.864z" fill="currentColor"></path><path d="M20.583 9.5a.9.9 0 01-1.8 0V3.4h-13.2v17.2H11l.092.005a.9.9 0 010 1.79L11 22.4H5.183a1.4 1.4 0 01-1.4-1.4V3a1.4 1.4 0 011.4-1.4h14a1.4 1.4 0 011.4 1.4v6.5z" fill="currentColor"></path><path d="M19.83 16.29a2.949 2.949 0 10-5.9 0 2.949 2.949 0 105.9 0zm1.8 0c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75 2.13-4.75 4.75-4.75 4.75 2.13 4.75 4.75z" fill="currentColor"></path><path d="M20.19 18.63a.89.89 0 00-1.27.02.89.89 0 00.02 1.27l1.93 1.87c.36.35.93.34 1.27-.02a.89.89 0 00-.02-1.27l-1.93-1.87z" fill="currentColor"></path></svg>
                          </span>
                          </OverlayTrigger> */}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

          </>
        }

        <div className="hidden grid grid-cols-[600px_1fr] gap-[30px] items-start dashboard-columns">
          <div className=" sticky top-10">
            <WalletDashboardCard />
          </div>
          <div>
            {
              loading ? (
                <DashboardListingSkeleton />
              ) :
                <>
                  {chains.map((chain, index) => (
                    <div className="tokens-section mb-4" key={index}>
                      <div className="tokens-header justify-between">
                        <h3 className="flex items-center gap-2 m-0">
                          {chain.name} <img src={chain.image} alt={`${chain.name} icon`} className="w-4 h-4" /> </h3>
                      </div>

                      <div className="tokens-list">
                        <div className="space-y-3">
                          {chain.coins.map((token, tokenIndex) => {
                            console.log('Token ID:', token.id, 'Is refreshing:', refreshingCoins[token.id]);
                            return (
                              <div key={tokenIndex} className="flex items-center gap-2 relative">

                                <div
                                  className="token-item flex-1 cursor-pointer transition-colors rounded-lg p-2"
                                  onClick={() => {
                                    setSelectedToken(token)
                                    setSelectedChain(chain)
                                    navigate(`/coin/${chain.id}/${token.id}`)
                                  }}
                                >
                                  {/* Left: Logo + Info */}
                                  <div className="flex items-center gap-4">
                                    <img
                                      src={token.image}
                                      alt={token.name}
                                      className="min-w-[40px] min-h-[40px] w-10 h-10 rounded-full coin-icon"
                                    />
                                    <div>
                                      <div className="flex items-center gap-2 mb-2">
                                        <span className="text-primary text-nowrap">{token.name}</span>
                                        <span className="font-medium">{chain.symbol}</span>

                                        {/* Transaction Status Loader */}
                                        {/* {token.transactionStatus && (
                                          <TransactionStatusLoader transactionStatus={token.transactionStatus} />
                                        )} */}

                                        {/* <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip id={`price-${token.id}`}>Click to view on CoinMarketCap</Tooltip>}
                                        > */}
                                        <span
                                          className="font-medium text-nowrap cursor-pointer hover:text-primary transition-colors"
                                        // onClick={(e) => {
                                        //   e.stopPropagation()
                                        //   openCoinMarketCap(token)
                                        // }}
                                        >
                                          {`$ ${parseFloat(token.price?.price).toFixed(2)}`}
                                        </span>
                                        {/* </OverlayTrigger> */}
                                        <span>|</span>
                                        {/* <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip id={`change-${token.id}`}>Click to view on CoinMarketCap</Tooltip>}
                                        > */}
                                        <span
                                          className="flex items-center lg:w-auto w-full cursor-pointer hover:text-primary transition-colors"
                                        // onClick={(e) => {
                                        //   e.stopPropagation()
                                        //   openCoinMarketCap(token)
                                        // }}
                                        >
                                          {parseFloat(token.price?.change || 0).toFixed(2)} %
                                          {token.price?.change >= 0 ?
                                            <TrendingUp color="green" className="ml-[6px] w-4 h-4 ml-1" />
                                            :
                                            <TrendingDown color="red" className="ml-[6px] w-4 h-4 ml-1" />}
                                        </span>
                                        {/* </OverlayTrigger> */}
                                      </div>
                                      <div className="text-xs break-all">{token.walletAddress}</div>
                                    </div>
                                  </div>

                                  <div className={`flex items-center gap-4 lg:w-auto w-full ${blurred ? 'secret blurred' : ''} `}>
                                    <div className={` text-right }`}>
                                      <div className={`flex items-center justify-end gap-2`}>
                                        {token.isShieldable && (
                                          <OverlayTrigger
                                            placement="top"
                                            overlay={<Tooltip id="shield-tooltip">
                                              {token.transactionStatus ?
                                                `${token.transactionStatus.type} in progress...` :
                                                (mode === 'public' ? 'Shield' : 'Unshield')
                                              }
                                            </Tooltip>}
                                          >
                                            <button
                                              className="sheild-icon btn-icon"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                if (!token.transactionStatus) {
                                                  handleShieldUnshieldClick(token)
                                                }
                                              }}
                                              disabled={!!token.transactionStatus}
                                              style={{
                                                cursor: token.transactionStatus ? "not-allowed" : "pointer",
                                                opacity: token.transactionStatus ? 0.5 : 1
                                              }}
                                            >
                                              {mode === 'public' ? <ShieldIconSvg className="w-5 h-5" /> : <UnshieldIconSvg className="w-5 h-5" />}
                                            </button>
                                          </OverlayTrigger>
                                        )}
                                        <OverlayTrigger
                                          placement="top"
                                          overlay={<Tooltip id="refresh-tooltip">{"Refresh"}</Tooltip>}
                                        >
                                          <button
                                            className="btn-icon"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleCoinRefresh(token.id)
                                            }}
                                            disabled={refreshingCoins[token.id]}
                                            style={{
                                              backgroundColor: refreshingCoins[token.id] ? '#f0f0f0' : 'transparent',
                                              opacity: refreshingCoins[token.id] ? 0.7 : 1
                                            }}
                                          >
                                            <RefreshCw className={`w-6 h-6 ${refreshingCoins[token.id] ? 'refresh-rotating' : ''}`} />
                                          </button>
                                        </OverlayTrigger>
                                        <span>{formatCryptoBalance(token.balance, token.symbol)}</span>
                                      </div>
                                      <div className="">≈ {formatUSDBalance(token.balance * token.price?.price)}</div>
                                    </div>
                                    <div onClick={(e) => e.stopPropagation()}>
                                      <OverlayTrigger
                                        placement="top"
                                        overlay={<Tooltip id="refresh-tooltip">{"View details"}</Tooltip>}
                                      >
                                        <span className="px-2"
                                          onClick={() => {
                                            setSelectedToken(token)
                                            setSelectedChain(chain)
                                            // setCoinDetailsModalOpen(true)
                                            navigate(`/coin/${chain.id}/${token.id}`)
                                          }}
                                        >

                                          <svg class="bn-svg" width={20} height={20} alt="Detail" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M15.863 5.363a.9.9 0 011.274 1.274l-4.147 4.146a1.4 1.4 0 01-1.98 0L10 9.773l-1.863 1.864a.9.9 0 01-1.274-1.274L9.01 8.217l.106-.096a1.4 1.4 0 011.768 0l.106.096L12 9.227l3.863-3.864z" fill="currentColor"></path><path d="M20.583 9.5a.9.9 0 01-1.8 0V3.4h-13.2v17.2H11l.092.005a.9.9 0 010 1.79L11 22.4H5.183a1.4 1.4 0 01-1.4-1.4V3a1.4 1.4 0 011.4-1.4h14a1.4 1.4 0 011.4 1.4v6.5z" fill="currentColor"></path><path d="M19.83 16.29a2.949 2.949 0 10-5.9 0 2.949 2.949 0 105.9 0zm1.8 0c0 2.62-2.13 4.75-4.75 4.75s-4.75-2.13-4.75-4.75 2.13-4.75 4.75-4.75 4.75 2.13 4.75 4.75z" fill="currentColor"></path><path d="M20.19 18.63a.89.89 0 00-1.27.02.89.89 0 00.02 1.27l1.93 1.87c.36.35.93.34 1.27-.02a.89.89 0 00-.02-1.27l-1.93-1.87z" fill="currentColor"></path></svg>
                                        </span>
                                      </OverlayTrigger>
                                      {/* <Dropdown align="end" drop="up" id="tour-action-menu">
                                        <Dropdown.Toggle
                                          variant="light"
                                          className="btn btn-icon border p-0"
                                        >
                                          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"><circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" /></g></svg>
                                        </Dropdown.Toggle>

                                        <Dropdown.Menu className="shadow rounded-3 coin-dropdown-menu">
                                          {mode === 'private' ? (
                                            // Private mode - Internal Transfer and History
                                            <>
                                              <Dropdown.Item onClick={() => {
                                                setPrivateTransferModalOpen(true)
                                                setSelectedToken(token)
                                              }}>Private Transfer</Dropdown.Item>
                                              <Dropdown.Item href={`/activity/${token.id}`}>History</Dropdown.Item>
                                              <Dropdown.Divider />
                                              <Dropdown.Item
                                                onClick={() => {
                                                  setSelectedToken(token)
                                                  setSelectedChain(chain)
                                                  // setCoinDetailsModalOpen(true)
                                                  navigate(`/coin/${chain.id}/${token.id}`)
                                                }}
                                                className="d-flex align-items-center"
                                              >
                                                <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                                Coin Details & Chart
                                              </Dropdown.Item>
                                              <Dropdown.Item onClick={() => openCoinMarketCap(token)} className="d-flex align-items-center">
                                                <ExternalLink className="w-4 h-4 me-2" />
                                                View on CoinMarketCap
                                              </Dropdown.Item>
                                            </>
                                          ) : (
                                            // Public mode - all options
                                            <>
                                              <Dropdown.Item onClick={() => {
                                                setSelectedToken(token)
                                                setSendModalOpen(true)
                                              }}>Send</Dropdown.Item>
                                              <Dropdown.Item onClick={() => {
                                                setReceiveModalOpen(true)
                                                setSelectedToken(token)
                                              }}>Receive</Dropdown.Item>
                                              <Dropdown.Item href={`/activity/${token.id}`}>History</Dropdown.Item>
                                              <Dropdown.Divider />
                                              <Dropdown.Item
                                                onClick={() => {
                                                  setSelectedToken(token)
                                                  setSelectedChain(chain)
                                                  // setCoinDetailsModalOpen(true)
                                                  navigate(`/coin/${chain.id}/${token.id}`)
                                                }}
                                                className="d-flex align-items-center"
                                              >
                                                <svg className="w-4 h-4 me-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                </svg>
                                                Coin Details & Chart
                                              </Dropdown.Item>
                                              <Dropdown.Item onClick={() => openCoinMarketCap(token)} className="d-flex align-items-center">
                                                <ExternalLink className="w-4 h-4 me-2" />
                                                View on CoinMarketCap
                                              </Dropdown.Item>
                                            </>
                                          )}
                                        </Dropdown.Menu>
                                      </Dropdown> */}
                                    </div>
                                  </div>
                                </div>

                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
            }
          </div>
        </div>


      </div>
      {sendModalOpen && <SendModal onClose={() => setSendModalOpen(false)} selectedToken={selectedToken} />}
      {welcomeModalOpen && <WelcomeBonusModal show={welcomeModalOpen} onHide={handleCloseWelcomeModal} />}
      {receiveModalOpen && <ReceiveModal

        selectedToken={selectedToken}
        onClose={() => setReceiveModalOpen(false)} />}
      {shieldUnshieldModalOpen && <ShieldModal onClose={() => setShieldUnshieldModalOpen(false)} selectedToken={selectedToken} mode={mode} />}
      {privateTransferModalOpen && <PrivateTransferModal onClose={() => setPrivateTransferModalOpen(false)} selectedToken={selectedToken} />}
      <CoinDetailsModal
        show={coinDetailsModalOpen}
        onHide={() => setCoinDetailsModalOpen(false)}
        coin={selectedToken}
        chain={selectedChain}
      />
    </>
  )
}

export default WalletDashboard;